/**
 * #976: client-side shapes for the card annotation API.
 *
 * Targets and records are the consumers' own types: #978's
 * `src/lib/cardNotes/types.ts` and #979's `src/lib/cardSources/types.ts`
 * (identical target shapes, pinned in `consumerContract.test.ts`).
 *
 * `polymarket` means INTERNATIONAL Polymarket. Polymarket US is a separate
 * venue (`polymarket_us`) and is not a tracker-card venue.
 */
import type { CardNoteTarget, CardNoteVenue } from '../cardNotes/types';

export type CardVenue = CardNoteVenue;
export type CardTarget = CardNoteTarget;

/**
 * The scope literal of a manager or admin's READ of a trader's notes (no page token: nothing is
 * written, so nothing is fenced). The server accepts it only on the oversight read route.
 */
export const TRADER_VIEW_SCOPE = 'trader-view';

export type CardUnavailableReason = 'no_exact_account_identity';

/** A target the server could not tie to an explicit venue account: its control should be disabled. */
export interface CardUnavailableTarget {
  target: CardTarget;
  reason: CardUnavailableReason;
}

/** Reports cards to disable. Additive: a consumer that ignores it keeps working. */
export interface CardAvailabilityOptions {
  onUnavailable?: (targets: readonly CardUnavailableTarget[]) => void;
}

export interface CardScopeResult {
  scope: string | null;
  venues: Partial<Record<CardVenue, { available: true } | { available: false; reason: CardUnavailableReason }>>;
}

/** Where requests go. The browser default is `apiFetch` + the auth session epoch. */
export interface CardAnnotationTransport {
  send(path: string, init: RequestInit): Promise<Response>;
  /** Changes on login, logout or account switch; a reply from an older epoch is dropped. */
  epoch(): number;
}
